import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ImageIcon, ArrowRight, Settings, Monitor, Sliders, Zap,
} from 'lucide-react'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { useAuth } from '../hooks/useAuth'
import { imagecompoAPI } from '../lib/api'
import PageHeader from '../components/PageHeader'
import SubmissionStatusCard from '../components/SubmissionStatusCard'
import { useImagecompoSocket } from '../lib/imagecompoSocket'
import ImagecompoNav from '@/components/ImagecompoNav'
import CompoStatusStrip from '@/components/CompoStatusStrip'

const IC_FASE = {
  draft: 'Draft', submissions: 'Inschrijvingen open', live: 'Show bezig',
  pauze: 'Pauze', reveal: 'Reveal', done: 'Klaar',
}
function icNextAction({ show, sub }) {
  if (!show) return null
  if (show.phase === 'submissions') return sub?.can_submit ? 'Lever je image in' : (sub?.submission ? 'Ingezonden — wachten op start' : 'Inschrijvingen open')
  if (show.phase === 'live')        return 'Beelden op de show'
  if (show.phase === 'pauze')       return 'Pauze — wachten op reveal'
  if (show.phase === 'reveal')      return 'Reveal loopt'
  if (show.phase === 'done')        return 'Show voorbij'
  return null
}
import { Upload, Palette, Vote as VoteIcon, Image as ImgIcon } from 'lucide-react'

export default function ImageCompoPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin'
  const [sub, setSub] = useState(null)

  const load = () => imagecompoAPI.getMySubmission().then(r => setSub(r.data)).catch(() => {})
  useEffect(() => { load() }, [])
  useImagecompoSocket(load)

  const show = sub?.show
  const submission = sub?.submission
  const eyebrow = show ? `Show · ${show.name}${show.edition ? ` — ${show.edition}` : ''}` : 'Show'

  const liveMsg = show?.phase === 'live'
    ? 'Show is bezig'
    : show?.phase === 'reveal'
      ? 'Reveal loopt'
      : null

  const mineLabel = submission
    ? (submission.team_name ? `Team "${submission.team_name}" ingezonden` : 'Ingezonden')
    : (show?.phase === 'submissions' ? 'Nog niet ingezonden' : null)

  return (
    <div className="space-y-6">
      <ImagecompoNav />
      <CompoStatusStrip
        phase={show?.phase}
        phaseLabel={show?.phase ? (IC_FASE[show.phase] || show.phase) : null}
        mineLabel={mineLabel}
        nextAction={icNextAction({ show, sub })}
        closesAt={show?.submissions_close_at || show?.phase_ends_at || null}
        accent="#FFB57A"
      />
      <PageHeader
        icon={ImageIcon}
        eyebrow={eyebrow}
        title="AI Image Compo"
        subtitle="AI-image wedstrijd — inzendingen, admin punten en live reveal op de beamer."
        actions={isAdmin && (
          <Button asChild>
            <a href="/imagecompo/stage" target="_blank" rel="noopener">
              <Monitor className="h-4 w-4 mr-2" />Open stage
            </a>
          </Button>
        )}
      />

      {liveMsg && (
        <div className="rounded-md border border-border bg-card px-4 py-3 flex items-center gap-4 flex-wrap"
             style={{ borderLeft: '2px solid rgb(var(--acid-rgb))' }}>
          <span className="live-dot">Nu</span>
          <span className="flex-1 text-sm text-foreground">{liveMsg}</span>
        </div>
      )}

      <RulesCard rules={sub?.show?.rules_text} />

      {submission && (
        <SubmissionStatusCard
          submission={submission}
          actionTo="/imagecompo/inleveren"
          subtitleFn={s => s.proposed_team_name || 'geen team naam'}
        />
      )}

      {show?.submissions_open && !submission && (
        <Card className="border-border" style={{ borderLeft: '2px solid rgb(var(--acid-rgb))' }}>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
              <ImageIcon className="h-5 w-5 text-acid" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-widest text-acid">Doe je mee?</div>
              <div className="text-lg font-semibold tracking-tight">Lever je AI-image in</div>
              <div className="text-sm text-muted-foreground">
                Kies of maak een team en upload jouw beeld. Admin keurt de inzending goed voor de show.
              </div>
            </div>
            <Button asChild size="lg">
              <Link to="/imagecompo/inleveren">Inschrijven <ArrowRight className="h-4 w-4 ml-1" /></Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {!show && (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            Nog geen actieve show.{isAdmin && ' Maak er één aan via Admin.'}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function RulesCard({ rules }) {
  if (!rules) return null
  return (
    <div className="rounded-md border border-border bg-card p-4"
         style={{ borderLeft: '2px solid rgb(var(--peach-rgb))' }}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-peach mb-2">Regels & uitleg</div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed">{rules}</div>
    </div>
  )
}